/**
 * Career Hooks
 *
 * React Query hooks for opportunities and CV management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../providers/SdkProvider';
import {
  CareerService,
  Opportunity,
  OpportunityFilters,
  CVData,
  GenerateCVInput,
} from '../services/career.service';

// ═══════════════════════════════════════════════════════════════════════
// QUERY KEYS
// ═══════════════════════════════════════════════════════════════════════

export const careerKeys = {
  all: ['career'] as const,
  opportunities: (filters?: OpportunityFilters) =>
    [...careerKeys.all, 'opportunities', filters] as const,
  opportunity: (id: string) => [...careerKeys.all, 'opportunity', id] as const,
  savedOpportunities: () => [...careerKeys.all, 'saved'] as const,
  cv: () => [...careerKeys.all, 'cv'] as const,
};

// ═══════════════════════════════════════════════════════════════════════
// HOOKS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Get opportunities
 */
export function useOpportunities(filters?: OpportunityFilters) {
  const client = useApiClient();
  const service = new CareerService(client);

  return useQuery<{ opportunities: Opportunity[]; total: number }>({
    queryKey: careerKeys.opportunities(filters),
    queryFn: () => service.getOpportunities(filters),
  });
}

/**
 * Get single opportunity
 */
export function useOpportunity(id: string) {
  const client = useApiClient();
  const service = new CareerService(client);

  return useQuery<Opportunity>({
    queryKey: careerKeys.opportunity(id),
    queryFn: () => service.getOpportunity(id),
    enabled: !!id,
  });
}

/**
 * Get saved opportunities
 */
export function useSavedOpportunities() {
  const client = useApiClient();
  const service = new CareerService(client);

  return useQuery<Opportunity[]>({
    queryKey: careerKeys.savedOpportunities(),
    queryFn: () => service.getSavedOpportunities(),
  });
}

/**
 * Express interest in opportunity
 */
export function useExpressInterest() {
  const client = useApiClient();
  const service = new CareerService(client);
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean }, Error, { opportunityId: string; coverNote?: string }>({
    mutationFn: ({ opportunityId, coverNote }) => service.expressInterest(opportunityId, coverNote),
    onSuccess: (_data, { opportunityId }) => {
      // Update opportunity in cache to show applied
      queryClient.setQueryData<Opportunity>(careerKeys.opportunity(opportunityId), (old) =>
        old ? { ...old, hasApplied: true } : old
      );
      // Invalidate opportunities list
      queryClient.invalidateQueries({ queryKey: careerKeys.opportunities() });
    },
  });
}

/**
 * Save opportunity for later
 */
export function useSaveOpportunity() {
  const client = useApiClient();
  const service = new CareerService(client);
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean }, Error, string>({
    mutationFn: (opportunityId) => service.saveOpportunity(opportunityId),
    onSuccess: (_data, opportunityId) => {
      // Update opportunity in cache
      queryClient.setQueryData<Opportunity>(careerKeys.opportunity(opportunityId), (old) =>
        old ? { ...old, isSaved: true } : old
      );
      // Invalidate saved list
      queryClient.invalidateQueries({ queryKey: careerKeys.savedOpportunities() });
    },
  });
}

/**
 * Get CV data
 */
export function useCV() {
  const client = useApiClient();
  const service = new CareerService(client);

  return useQuery<CVData>({
    queryKey: careerKeys.cv(),
    queryFn: () => service.getCV(),
  });
}

/**
 * Generate CV
 */
export function useGenerateCV() {
  const client = useApiClient();
  const service = new CareerService(client);
  const queryClient = useQueryClient();

  return useMutation<CVData, Error, GenerateCVInput | undefined>({
    mutationFn: (input) => service.generateCV(input),
    onSuccess: (data) => {
      queryClient.setQueryData(careerKeys.cv(), data);
    },
  });
}

/**
 * Download CV
 */
export function useDownloadCV() {
  const client = useApiClient();
  const service = new CareerService(client);

  return useMutation<{ downloadUrl: string }, Error, string>({
    mutationFn: (format) => service.downloadCV(format),
  });
}

// Re-export types
export type { Opportunity, OpportunityFilters, CVData, GenerateCVInput };
